import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store.js';
import { Button, Input, Select } from '../../components/ui.jsx';
import { AuthShell } from './LoginPage.jsx';

export default function SignupPage() {
  const navigate = useNavigate();
  const { signup } = useAuthStore();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    company_name: '', abn: '', industry: '', headcount_band: '11-50',
    full_name: '', email: '', job_title: '', password: '', accepted_terms: false
  });

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null); setLoading(true);
    try {
      await signup(form);
      navigate('/setup/organization?welcome=1');
    } catch (err) {
      setError(err.message || 'Signup failed');
    } finally { setLoading(false); }
  };

  return (
    <AuthShell wide>
      <h1 className="text-2xl font-semibold text-ink-900 mb-1">Start your Skillbase workspace</h1>
      <p className="text-sm text-ink-500 mb-6">Free 14-day trial. No card required to get started.</p>

      <form onSubmit={onSubmit} className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Company name" required value={form.company_name} onChange={e => update('company_name', e.target.value)} />
          <Input label="ABN (optional)" value={form.abn} onChange={e => update('abn', e.target.value)} hint="11-digit Australian Business Number" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Industry (optional)" value={form.industry} onChange={e => update('industry', e.target.value)} />
          <Select label="Headcount" value={form.headcount_band} onChange={e => update('headcount_band', e.target.value)}
            options={[
              { value: '1-10', label: '1–10 employees' },
              { value: '11-50', label: '11–50 employees' },
              { value: '51-200', label: '51–200 employees' },
              { value: '201-500', label: '201–500 employees' },
              { value: '501-1000', label: '501–1,000 employees' },
              { value: '1001-5000', label: '1,001–5,000 employees' },
              { value: '5000+', label: '5,000+ employees' }
            ]} />
        </div>

        <div className="border-t border-ink-200 pt-5">
          <h3 className="text-sm font-semibold text-ink-900 mb-3">Your account</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Your name" required autoComplete="name" value={form.full_name} onChange={e => update('full_name', e.target.value)} />
            <Input label="Job title" value={form.job_title} onChange={e => update('job_title', e.target.value)} />
            <Input label="Work email" required type="email" autoComplete="email" value={form.email} onChange={e => update('email', e.target.value)} />
            <Input label="Password" required type="password" autoComplete="new-password" value={form.password} onChange={e => update('password', e.target.value)} hint="12+ chars, mixed case, number, symbol" />
          </div>
        </div>

        <label className="flex items-start gap-2 text-sm text-ink-600">
          <input type="checkbox" checked={form.accepted_terms} onChange={e => update('accepted_terms', e.target.checked)} className="mt-0.5" required />
          <span>I agree to the <Link to="/legal/terms" className="text-primary-700">Terms of Service</Link> and <Link to="/legal/privacy" className="text-primary-700">Privacy Policy</Link>.</span>
        </label>

        {error ? <p className="text-sm text-danger">{error}</p> : null}
        <Button type="submit" loading={loading} className="w-full" size="lg">Create workspace</Button>
      </form>

      <p className="text-sm text-ink-500 mt-6 text-center">
        Already have an account? <Link to="/login" className="text-primary-700 font-medium">Sign in</Link>
      </p>
    </AuthShell>
  );
}
